import { _js } from '@ifixit/utils/localize';
import {
   Dialog,
   DialogClose,
   DialogContentPrimitive,
   DialogOverlay,
   DialogPortal,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from '@ifixit/ui/components/icon';
import { toast } from '@ifixit/ui/components/toast';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useRef, useState } from 'react';
import {
   copyImageWithToast,
   openImageCrop,
   openImageMarkers,
   type LegacyEditedImage,
} from 'Shared/editor/legacy_image_dialogs';
import { IconActionButton } from 'Shared/editor/IconActionButton';
import { movedItem, useDragReorder } from 'Shared/editor/use_drag_reorder';
import type { ApiStepMedia } from './types';
import type { ReactNode } from 'react';

const MAX_STEP_IMAGES = 3;

/** Rendition URLs by size name; null when the size isn't stored. */
type SlotRenditions = {
   thumbnail: string | null;
   standard: string | null;
   medium: string | null;
   large: string | null;
   original: string | null;
};

type MediaSlotImage = {
   imageid: number;
   renditions: SlotRenditions;
};

export type MediaSelection =
   | { type: 'image'; images: MediaSlotImage[] }
   | { type: 'object'; objectid: number | null; thumbUrl: string | null };

/** Tiles render ~208px wide, so standard (300px) keeps them sharp. */
function tileUrl(image: MediaSlotImage): string | null {
   const sizes = image.renditions;
   return sizes.standard ?? sizes.medium ?? sizes.thumbnail ?? sizes.original;
}

/** The big preview beside the tiles (~650px wide on desktop). */
function previewUrl(image: MediaSlotImage): string | null {
   const sizes = image.renditions;
   return sizes.large ?? sizes.medium ?? sizes.standard ?? sizes.original;
}

/** Largest rendition, for the full-screen lightbox. */
function fullSizeUrl(image: MediaSlotImage): string | null {
   const sizes = image.renditions;
   return sizes.original ?? sizes.large ?? sizes.medium ?? sizes.standard;
}

function editedSlotImage(edited: LegacyEditedImage): MediaSlotImage {
   return {
      imageid: edited.imageid,
      renditions: {
         thumbnail: edited.thumbnail,
         standard: edited.standard,
         medium: edited.medium,
         large: null,
         original: edited.original,
      },
   };
}

/** Seeds the selector from the media shape GET /guides returns. */
export function mediaSelectionFromApi(media: ApiStepMedia | undefined): MediaSelection {
   if (!media || media.type === 'image') {
      const images = media?.type === 'image' ? media.data : [];
      return {
         type: 'image',
         images: (images ?? []).map(image => ({
            imageid: image.id,
            renditions: {
               thumbnail: image.thumbnail ?? null,
               standard: image.standard ?? null,
               medium: image.medium ?? null,
               large: image.large ?? null,
               original: image.original ?? null,
            },
         })),
      };
   }
   // Video info carries its poster frame as a nested image info object;
   // embeds may not have one. The frame renders ~576px wide, so prefer the
   // larger renditions (thumbnail is 300px and blurs at that size).
   const poster = media.data?.image as
      | { thumbnail?: string; standard?: string; medium?: string; original?: string }
      | null
      | undefined;
   return {
      type: 'object',
      objectid: media.data?.objectid ?? null,
      thumbUrl: realRendition(
         poster?.medium,
         poster?.standard,
         poster?.thumbnail,
         poster?.original
      ),
   };
}

/**
 * The first rendition that is an actual image. Embed thumbnails fill their
 * missing sizes with the shared NoImage art instead of omitting them, so a
 * larger-first preference would happily pick a placeholder over the real
 * (smaller) frame.
 */
function realRendition(...urls: (string | null | undefined)[]): string | null {
   return urls.find(url => url && !url.includes('/NoImage')) ?? null;
}

async function pickMedia(accepts: string, hiddenGlobalIds: string[] = []) {
   const { openMediaManager } = await import('Shared/MediaManager');
   return openMediaManager({ accepts, hiddenGlobalIds });
}

// The media block is width-driven: the 4:3 preview fills its column, so its
// height is a fixed fraction of the row width (preview ≈ 75% of the row and
// its height is 3/4 of that → ~0.5625 × row width). To leave room for the
// step's lines on a short viewport we cap the *width*, which shrinks the
// preview and its filmstrip tiles together, keeping their careful alignment.
const PREVIEW_HEIGHT_PER_WIDTH = 0.5625;
// Vertical space kept below the media so at least ~4 lines and the sticky
// action bar stay in view: 4 line rows (~34px) plus the footer and gaps.
const RESERVE_BELOW_MEDIA_PX = 220;

/**
 * The nearest scrolling ancestor — at md+ the editor card, which the step
 * rail sizes to the viewport. The 4-line reserve must fit inside THIS box:
 * measuring the window would let the lines fall under the card's own fold.
 * Null on phones, where the card grows with its content and the viewport
 * is the only fold.
 */
function scrollBoxOf(node: HTMLElement): HTMLElement | null {
   for (let el = node.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).overflowY === 'auto') {
         return el;
      }
   }
   return null;
}
// Never shrink past this — below it the image is too small to work from, and
// the page can scroll the last line or two instead.
const MIN_MEDIA_WIDTH_PX = 260;
// The image mode's natural cap (max-w-4xl). At or above it there's enough
// height for the full-size view, so the constraint is released entirely.
const MAX_MEDIA_WIDTH_PX = 896;

/**
 * A max-width (px) for the step media so a short viewport still shows ~4 step
 * lines below it, or null on a tall enough viewport to use the full-size
 * view. Measured from the media element's top, recomputed on resize and when
 * the element changes (mode switch, tab becoming visible); not on scroll, so
 * the size is stable while the author reads down the step.
 */
function useCompactMediaWidth(node: HTMLElement | null): number | null {
   const [maxWidth, setMaxWidth] = useState<number | null>(null);
   useEffect(() => {
      if (!node) {
         return;
      }
      let frame = 0;
      const measure = () => {
         frame = 0;
         // Behind another tab (forceMount keeps this mounted): rects are zero,
         // so any measurement is garbage. The observer re-fires on reveal.
         if (node.offsetParent === null) {
            return;
         }
         const top = Math.max(0, node.getBoundingClientRect().top);
         const foldBottom = Math.min(
            window.innerHeight,
            scrollBoxOf(node)?.getBoundingClientRect().bottom ?? Infinity
         );
         const heightBudget = foldBottom - top - RESERVE_BELOW_MEDIA_PX;
         const width = heightBudget / PREVIEW_HEIGHT_PER_WIDTH;
         setMaxWidth(
            width < MAX_MEDIA_WIDTH_PX ? Math.max(MIN_MEDIA_WIDTH_PX, Math.round(width)) : null
         );
      };
      const schedule = () => {
         frame ||= requestAnimationFrame(measure);
      };
      measure();
      window.addEventListener('resize', schedule);
      // Catches the tab becoming visible and layout shifts above the media.
      // Observing the node we also resize is safe: the width we set doesn't
      // move its top, so the recompute yields the same value and settles.
      const observer = new ResizeObserver(schedule);
      observer.observe(node);
      // The fold moves when the editor card is resized (the rail refits it
      // on viewport changes) even while the media's own box stays put.
      const scrollBox = scrollBoxOf(node);
      if (scrollBox) {
         observer.observe(scrollBox);
      }
      return () => {
         cancelAnimationFrame(frame);
         window.removeEventListener('resize', schedule);
         observer.disconnect();
      };
   }, [node]);
   return maxWidth;
}

/**
 * The step media input: up to 3 sortable images (a big preview with a
 * filmstrip of tiles beside it), or one video/embed. The image/video
 * switcher only shows while the step has no media. Media picking goes
 * through the React Media Manager against the guide_step_image /
 * guide_step_object filter collections.
 *
 * canAdd=false drops every picking affordance (the type switcher and the
 * add/choose tiles), leaving only the media already on the step — video
 * guides use it, where the step's visual is its slice of the guide video.
 */
export function StepMediaSelector({
   media,
   canAdd = true,
   onChange,
}: {
   media: MediaSelection;
   canAdd?: boolean;
   onChange: (media: MediaSelection) => void;
}) {
   const images = media.type === 'image' ? media.images : [];
   // The picker and the legacy image dialogs hold the UI across an await
   // while the images prop can change underneath (a conflict reload
   // adopting the server step, another slot's edit landing). The async
   // flows read the list through this ref at resolve time, so a stale
   // snapshot can't clobber or resurrect those changes.
   const imagesRef = useRef(images);
   useEffect(() => {
      imagesRef.current = images;
   });
   // The rendered media container, measured to fit the viewport height so a
   // short screen keeps the step's lines in view (null while the tall-view
   // full size is used).
   const [mediaNode, setMediaNode] = useState<HTMLDivElement | null>(null);
   const compactMediaWidth = useCompactMediaWidth(mediaNode);
   const drag = useDragReorder((from, to) =>
      onChange({ type: 'image', images: movedItem(images, from, to) })
   );
   // Which slot the big preview shows, and so which one replacing overwrites.
   // Hovering, clicking, or focusing a tile aims it, and the tile carries a
   // ring while it holds it — the preview alone can't say which slot it came
   // from once a step's photos look alike. Keyed by imageid, not index, so a
   // drag reorder doesn't swap the preview.
   const [selectedImageid, setSelectedImageid] = useState<number | null>(null);
   const selectedIndex = Math.max(
      0,
      images.findIndex(image => image.imageid === selectedImageid)
   );
   const previewImage = images[selectedIndex];
   const previewImg = previewImage && (
      <img
         src={previewUrl(previewImage) ?? undefined}
         alt=""
         loading="lazy"
         className="aspect-[4/3] w-full object-cover"
      />
   );
   // The image open full-frame in the browser (the magnifier), for
   // inspecting details like an individual screw head; null when closed.
   const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
   // The slot showing a spinner while a cropped image is produced in the
   // background (crop dialog closed, image not yet ready): 'new' for one
   // being added, or the imageid of one being re-cropped in place.
   const [processingSlot, setProcessingSlot] = useState<'new' | number | null>(null);
   // `pickMedia` code-splits the media manager, so the first click sits on a
   // cold import with nothing on screen yet — long enough to click again and
   // open two pickers.
   const [isPicking, setIsPicking] = useState(false);

   // No cross-type veto needed: the switcher only renders while the step
   // has no media, so switching can never discard anything.
   const switchToImages = () => {
      if (media.type === 'object') {
         onChange({ type: 'image', images: [] });
      }
   };

   const switchToObject = () => {
      if (media.type === 'image') {
         onChange({ type: 'object', objectid: null, thumbUrl: null });
      }
   };

   // Swaps `replacement` into the slot `imageid` holds, read from the live list
   // rather than a snapshot so edits made during the dialog survive. Null when
   // that slot is gone — a conflict reload dropped it, or the author deleted
   // the photo before acting. Every caller crosses an async gap holding an id,
   // and a bare .map would quietly return the list unchanged instead: the
   // action then reports success while doing nothing.
   const swapSlot = (imageid: number, replacement: MediaSlotImage): MediaSlotImage[] | null => {
      const current = imagesRef.current;
      return current.some(image => image.imageid === imageid)
         ? current.map(image => (image.imageid === imageid ? replacement : image))
         : null;
   };

   // Picks a guide_step_image and resolves it to a slot image, honoring the
   // filter's 4:3 requirement: the picker returns a 'crop' verdict for an
   // off-ratio image, so route it through the crop dialog — which locks the
   // box to 4:3 — and keep the cropped copy, like v1's media targets and the
   // Intro tab's featured photo. `slot` labels the spinner shown while the
   // crop renders. Returns null on cancel, or when the editor fails to open
   // (toasted here).
   const pickStepImage = async (
      slot: 'new' | number,
      hiddenGlobalIds: string[]
   ): Promise<MediaSlotImage | null> => {
      const item = await pickMedia('guide_step_image', hiddenGlobalIds);
      if (!item || item.imageid === undefined) {
         return null;
      }
      // The picker hides images already on the step, but one can land on
      // another slot while it's open (a conflict reload, another slot's edit
      // resolving). Bail before the crop dialog so that race can't duplicate
      // a React list key — and so an off-ratio pick doesn't walk the author
      // through a crop only to be dropped.
      if (imagesRef.current.some(image => image.imageid === item.imageid)) {
         toast.error(_js('That photo is already on this step.'), { duration: 5000 });
         return null;
      }
      if (item.filter_state === 'crop') {
         try {
            const cropped = await openImageCrop(
               {
                  imageid: item.imageid,
                  thumbnail: item.thumbnail ?? null,
                  standard: item.standard ?? null,
                  medium: item.medium ?? null,
                  original: item.original ?? null,
               },
               'guide_step_image',
               () => setProcessingSlot(slot)
            );
            return cropped ? editedSlotImage(cropped) : null;
         } catch {
            toast.error(_js('The image editor did not open. Try again.'), { duration: 5000 });
            return null;
         } finally {
            setProcessingSlot(null);
         }
      }
      return {
         imageid: item.imageid,
         renditions: {
            thumbnail: item.thumbnail ?? null,
            standard: item.standard ?? null,
            medium: item.medium ?? null,
            large: null,
            original: item.original ?? null,
         },
      };
   };

   const addImage = async () => {
      if (isPicking) {
         return;
      }
      setIsPicking(true);
      let picked: MediaSlotImage | null = null;
      try {
         picked = await pickStepImage(
            'new',
            images.map(image => `image:${image.imageid}`)
         );
      } finally {
         setIsPicking(false);
      }
      if (!picked) {
         return;
      }
      onChange({ type: 'image', images: [...imagesRef.current, picked] });
   };

   // Clicking the big preview swaps a new pick into the selected slot. Every
   // image on the step is hidden from the picker, so the pick is always a new
   // one landing in `target`'s place.
   const replaceImage = async (target: MediaSlotImage) => {
      if (isPicking) {
         return;
      }
      setIsPicking(true);
      let picked: MediaSlotImage | null = null;
      try {
         picked = await pickStepImage(
            target.imageid,
            images.map(image => `image:${image.imageid}`)
         );
      } finally {
         setIsPicking(false);
      }
      if (!picked) {
         return;
      }
      // The slot can vanish mid-pick (a conflict reload dropping it); the pick
      // has nowhere to land, and the author can see the step changed under
      // them, so drop it quietly rather than toasting on top of that.
      const replaced = swapSlot(target.imageid, picked);
      if (!replaced) {
         return;
      }
      onChange({ type: 'image', images: replaced });
      // The selection still points at the replaced image, now gone; repoint it
      // so the preview keeps showing this slot instead of snapping to the first.
      setSelectedImageid(picked.imageid);
      // A replace is one click on the largest target on screen, and the photo
      // it discards can be expensive to reshoot — offer the same way back that
      // deleting a line does rather than leaving the swap silent. Undo swaps the
      // original back into whatever slot the pick now holds, read from the live
      // list at click time, so edits made after the replace (a reorder, another
      // slot's change) survive instead of being clobbered by a stale snapshot.
      toast.custom({
         title: _js('Photo replaced.'),
         action: {
            label: _js('Undo'),
            onClick: () => {
               // The replacement can be gone by the time this is clicked (the
               // author deleted it, or a conflict reload landed). Say so: this
               // is the way back from a destructive action, and a button that
               // quietly does nothing reads as the photo being unrecoverable.
               const restored = swapSlot(picked.imageid, target);
               if (!restored) {
                  toast.error(_js('That photo is no longer on this step, so nothing was undone.'), {
                     duration: 5000,
                  });
                  return;
               }
               onChange({ type: 'image', images: restored });
               setSelectedImageid(target.imageid);
            },
         },
      });
   };

   const pickObject = async () => {
      const item = await pickMedia('guide_step_object');
      if (!item || item.objectid === undefined) {
         return;
      }
      onChange({
         type: 'object',
         objectid: item.objectid,
         thumbUrl: realRendition(item.medium, item.standard, item.thumbnail),
      });
   };

   const removeImage = (imageid: number) => {
      onChange({ type: 'image', images: images.filter(image => image.imageid !== imageid) });
   };

   // Markers and crop mint a *new* image; swap it into the same slot and
   // let the usual step save persist the change.
   const editImage = async (image: MediaSlotImage, action: 'markers' | 'crop') => {
      const input = { imageid: image.imageid, ...image.renditions };
      let edited: LegacyEditedImage | null = null;
      try {
         edited =
            action === 'markers'
               ? await openImageMarkers(input)
               : await openImageCrop(input, 'guide_step_image', () =>
                    setProcessingSlot(image.imageid)
                 );
      } catch {
         toast.error(_js('The image editor did not open. Try again.'), { duration: 5000 });
         return;
      } finally {
         setProcessingSlot(null);
      }
      if (!edited) {
         return;
      }
      // The author sat through a dialog and saved; if the slot went away while
      // they did, the edited copy exists on the server but has nowhere to land.
      // Losing that silently looks like the editor dropped their work.
      const applied = swapSlot(image.imageid, editedSlotImage(edited));
      if (!applied) {
         toast.error(_js('That photo is no longer on this step, so the edit was not applied.'), {
            duration: 5000,
         });
         return;
      }
      onChange({ type: 'image', images: applied });
   };

   const hasMedia = media.type === 'image' ? images.length > 0 : media.objectid !== null;

   return (
      <div>
         {/* The type switcher only matters while the step has no media —
             once something is placed, hiding it saves vertical space for
             the image (removing the media brings it back). */}
         {!hasMedia && canAdd && (
            <div className="mb-3 flex gap-2">
               <MediaTypeButton active={media.type === 'image'} onClick={switchToImages}>
                  {_js('Images')}
               </MediaTypeButton>
               <MediaTypeButton active={media.type === 'object'} onClick={switchToObject}>
                  {_js('Video / Embed')}
               </MediaTypeButton>
            </div>
         )}

         {media.type === 'image' ? (
            // The tiles sit beside the preview (a filmstrip), not under it,
            // so they cost no vertical space. Together they fill the editor
            // column (870px at the widest viewport): the filmstrip is fixed
            // and the preview takes all the remaining width. On a short
            // viewport the measured max-width shrinks the whole row so the
            // step's lines stay visible below it.
            <div
               ref={setMediaNode}
               style={compactMediaWidth ? { maxWidth: compactMediaWidth } : undefined}
               className="flex flex-col max-w-4xl gap-2 sm:flex-row"
            >
               {previewImage && (
                  <div className="group relative min-w-0 flex-1 self-start overflow-hidden rounded-md border border-gray-300 bg-gray-50">
                     {/* Clicking the primary photo swaps in a replacement via
                         the media manager. The corner overlays (menu, magnify)
                         sit above this button and capture their own clicks. */}
                     {canAdd ? (
                        <button
                           type="button"
                           disabled={isPicking}
                           onClick={() => void replaceImage(previewImage)}
                           // The indicator rides an ::after overlay, not the
                           // button's own ring: an inset shadow paints in the
                           // background layer, so the photo filling this button
                           // covers it and the ring never shows. The wrapper
                           // clips to rounded corners, so it stays inset. And
                           // it lands on an arbitrary photo, where one color
                           // always loses — the gray token vanishes on
                           // mid-tones, white on the white-background shots
                           // most of these are — so paint white and near-black
                           // concentrically and let whichever reads, read.
                           className="group/replace relative block w-full cursor-pointer outline-none after:pointer-events-none after:absolute after:inset-0 after:content-[''] focus-visible:after:shadow-[inset_0_0_0_3px_var(--color-white),inset_0_0_0_5px_var(--color-gray-900)]"
                        >
                           {previewImg}
                           {/* Named to the slot: the preview alone can't say
                               which of a step's near-identical photos is about
                               to be overwritten. Scoped to this button's own
                               focus so tabbing to the magnifier doesn't label
                               it "Replace photo". */}
                           <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-black/60 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible/replace:opacity-100 pointer-coarse:opacity-100">
                              <FaIcon name="right-left" className="size-3.5" />
                              <span>
                                 {images.length > 1
                                    ? _js('Replace photo %1', String(selectedIndex + 1))
                                    : _js('Replace photo')}
                              </span>
                           </span>
                        </button>
                     ) : (
                        previewImg
                     )}
                     {/* Same actions as the tiles, applied to whichever
                         image the preview is showing. */}
                     <ImageMenu
                        onEdit={action => void editImage(previewImage, action)}
                        onCopy={() => void copyImageWithToast(previewImage.imageid)}
                        onRemove={() => removeImage(previewImage.imageid)}
                     />
                     <MagnifyButton onClick={() => setLightboxUrl(fullSizeUrl(previewImage))} />
                     {processingSlot === previewImage.imageid && <CropSpinner />}
                  </div>
               )}
               <div
                  // Beside a preview the column stretches to its height and
                  // Tiles keep a fixed 4:3 (their crop must not change with
                  // the tile count — a stretched tile reads as a different
                  // zoom level). Alignment with the preview's bottom edge
                  // comes from the width instead: with the preview at
                  // (100% - strip - gap) and both 4:3, a full stack of
                  // three tiles matches its height when the strip is
                  // 25% - 7px of the row. Wider when it's the only thing
                  // there (no image yet), so the Add tile doesn't look like
                  // an afterthought. On phones the row wraps under the
                  // preview, so the tiles run across it as a filmstrip
                  // instead of a narrow stack (the Add tile beside the
                  // thumbnails, not below them).
                  className={cn(
                     'flex shrink-0 flex-row gap-2 sm:flex-col',
                     previewImage ? 'w-full sm:w-[calc(25%-7px)]' : 'w-56'
                  )}
               >
                  {/* The hook only returns the move text; a list that offers
                      arrow-key reordering has to render it, or the photos
                      change places in silence. */}
                  <span aria-live="polite" className="sr-only">
                     {drag.announcement}
                  </span>
                  {drag.displayItems(images).map(({ item: image }, displayIndex) => (
                     <div
                        key={image.imageid}
                        {...drag.rowProps(displayIndex, { draggable: true })}
                        onMouseEnter={() => setSelectedImageid(image.imageid)}
                        onClick={() => setSelectedImageid(image.imageid)}
                        // Focus bubbles from the tile's own menu, grip, and
                        // magnifier, so tabbing through a tile aims the preview
                        // at it — the only way to reach slot 2 without a mouse.
                        onFocus={() => setSelectedImageid(image.imageid)}
                        // The brand ring is the only mark on the aimed slot;
                        // name it for readers too, since which slot is selected
                        // decides which photo a replace overwrites.
                        aria-current={image.imageid === previewImage?.imageid || undefined}
                        className={cn(
                           'group relative aspect-[4/3] min-w-0 flex-1 cursor-grab overflow-hidden rounded-md border border-gray-300 bg-gray-50 active:cursor-grabbing sm:flex-none',
                           drag.draggingIndex === displayIndex
                              ? 'opacity-60 ring-2 ring-brand-300'
                              : image.imageid === previewImage?.imageid && 'ring-2 ring-brand-500'
                        )}
                     >
                        {tileUrl(image) ? (
                           <img
                              src={tileUrl(image) ?? undefined}
                              alt=""
                              loading="lazy"
                              className="size-full object-cover"
                           />
                        ) : (
                           <div className="flex size-full items-center justify-center text-xs text-gray-500">
                              {_js('Image %1', String(image.imageid))}
                           </div>
                        )}
                        {/* Reorder is drag-only now (grab the tile); this menu
                            carries the per-image actions. */}
                        <ImageMenu
                           onEdit={action => void editImage(image, action)}
                           onCopy={() => void copyImageWithToast(image.imageid)}
                           onRemove={() => removeImage(image.imageid)}
                        />
                        <MagnifyButton onClick={() => setLightboxUrl(fullSizeUrl(image))} />
                        {/* Pressing the photo itself reorders with a mouse, but
                            that path is pointer-only and holds the tile against
                            page scroll on touch. The grip is the touch and
                            keyboard affordance every other reorderable list in
                            the editor already carries. One photo can't move. */}
                        {images.length > 1 && (
                           <span
                              {...drag.handleProps(displayIndex, {
                                 label: _js(
                                    'Reorder photo %1 (use the arrow keys to move it)',
                                    String(displayIndex + 1)
                                 ),
                              })}
                              className="absolute bottom-1 left-1 flex size-7 cursor-grab items-center justify-center rounded-md bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 active:cursor-grabbing pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
                           >
                              <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
                           </span>
                        )}
                        {processingSlot === image.imageid && <CropSpinner />}
                     </div>
                  ))}
                  {canAdd && images.length < MAX_STEP_IMAGES && (
                     <button
                        type="button"
                        onClick={() => void addImage()}
                        disabled={processingSlot === 'new'}
                        className="relative flex aspect-[4/3] min-w-0 flex-1 items-center justify-center rounded-md border border-dashed border-gray-400 text-gray-500 outline-none hover:border-gray-500 hover:text-gray-700 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-default sm:flex-none"
                     >
                        {processingSlot === 'new' ? (
                           <CropSpinner />
                        ) : (
                           <span className="flex flex-col items-center gap-1 text-xs">
                              <FaIcon name="plus" className="size-4" />
                              {_js('Add image')}
                           </span>
                        )}
                     </button>
                  )}
               </div>
            </div>
         ) : media.objectid === null ? (
            // The picker keeps the image-tile size; only placed media earns
            // the big frame below.
            <div className="grid max-w-xl grid-cols-3 gap-2">
               {canAdd && (
                  <button
                     type="button"
                     onClick={() => void pickObject()}
                     className="flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-gray-400 text-gray-500 outline-none hover:border-gray-500 hover:text-gray-700 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                     <span className="flex flex-col items-center gap-1 text-xs">
                        <FaIcon name="play" className="size-4" />
                        {_js('Choose video or embed')}
                     </span>
                  </button>
               )}
            </div>
         ) : (
            // The step's visual is this video, so it gets the same scale as
            // the image-mode preview instead of a filmstrip-sized tile. The
            // measured cap (bounded by its own max-w-xl) shrinks it on a short
            // viewport just like the image preview.
            <div
               ref={setMediaNode}
               style={
                  compactMediaWidth ? { maxWidth: Math.min(576, compactMediaWidth) } : undefined
               }
               className="group relative aspect-[4/3] w-full max-w-xl overflow-hidden rounded-md border border-gray-300 bg-gray-900"
            >
               {media.thumbUrl ? (
                  <img
                     src={media.thumbUrl}
                     alt=""
                     loading="lazy"
                     className="size-full object-cover"
                  />
               ) : (
                  <div className="flex size-full items-center justify-center text-xs text-gray-400">
                     {_js('Media #%1', String(media.objectid))}
                  </div>
               )}
               <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex size-10 items-center justify-center rounded-full bg-black/60 text-white">
                     <FaIcon name="play" className="size-4" />
                  </span>
               </span>
               <div className="absolute inset-x-0 bottom-0 flex justify-end bg-black/50 p-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <IconButton
                     label={_js('Remove media')}
                     icon="trash-can"
                     onClick={() => onChange({ type: 'object', objectid: null, thumbUrl: null })}
                  />
               </div>
            </div>
         )}

         {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      </div>
   );
}

/**
 * The magnifying glass on a step image: opens it full frame in the browser
 * for inspecting details. Sits opposite the corner menu; a real overlay
 * button (not a menu entry) so it's one click away.
 */
function MagnifyButton({ onClick }: { onClick: () => void }) {
   return (
      <button
         type="button"
         aria-label={_js('View full screen')}
         title={_js('View full screen')}
         // Stop the pointerdown from arming a drag on the tile.
         onPointerDown={event => event.stopPropagation()}
         onClick={onClick}
         // Touch devices have no hover to reveal the trigger, and 28px needs
         // the hit area grown past the icon rather than the icon itself.
         className="absolute left-1 top-1 flex size-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
      >
         <FaIcon name="magnifying-glass-plus" className="size-4" />
      </button>
   );
}

/**
 * The image full frame over the whole browser viewport. Clicking anywhere
 * dismisses it, as do the top-right close button and Escape (via the
 * dialog primitives, which also trap focus while it's open).
 */
function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) {
               onClose();
            }
         }}
      >
         <DialogPortal>
            {/* z-lightbox-overlay clears the site header (z-100), so "full frame"
                really means the whole browser viewport; content sits one tier
                above at z-lightbox so the stacking order is encoded in the values,
                not sibling render order. */}
            <DialogOverlay className="z-lightbox-overlay bg-black/90" />
            <DialogContentPrimitive
               aria-describedby={undefined}
               onClick={onClose}
               className="data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-lightbox flex items-center justify-center p-4 outline-none"
            >
               <DialogTitle className="sr-only">{_js('Step image')}</DialogTitle>
               <img src={url} alt="" className="max-h-full max-w-full object-contain" />
               <DialogClose
                  aria-label={_js('Close')}
                  className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-md bg-black/50 text-white opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
               >
                  <FaIcon name="xmark" className="size-5" />
               </DialogClose>
            </DialogContentPrimitive>
         </DialogPortal>
      </Dialog>
   );
}

/**
 * Per-image actions on a step image tile. Reorder is drag-only, so this is
 * a corner menu, not a button row. Markers and crop open the legacy image
 * dialogs; copy duplicates into the user's media library.
 */
function ImageMenu({
   onEdit,
   onCopy,
   onRemove,
}: {
   onEdit: (action: 'markers' | 'crop') => void;
   onCopy: () => void;
   onRemove: () => void;
}) {
   return (
      <DropdownMenu>
         <DropdownMenuTrigger asChild>
            <button
               type="button"
               aria-label={_js('Image actions')}
               // Stop the pointerdown from arming a drag on the tile.
               onPointerDown={event => event.stopPropagation()}
               // The open menu renders in a portal, so neither the hover nor
               // the focus-within that reveal this trigger survive opening it;
               // without the open state it fades out from under its own menu.
               className="absolute right-1 top-1 flex size-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 data-[state=open]:opacity-100 pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
            >
               <FaIcon name="ellipsis" className="size-4" />
            </button>
         </DropdownMenuTrigger>
         <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit('markers')}>
               <FaIcon name="circle-dot" className="size-3.5" />
               {_js('Markers')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onEdit('crop')}>
               <FaIcon name="crop-simple" className="size-3.5" />
               {_js('Crop')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onCopy}>
               <FaIcon name="copy" className="size-3.5" />
               {_js('Copy to media library')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onRemove}>
               <FaIcon name="trash-can" className="size-3.5" />
               {_js('Delete')}
            </DropdownMenuItem>
         </DropdownMenuContent>
      </DropdownMenu>
   );
}

/**
 * A spinner filling an image slot while its cropped image is produced in the
 * background — shown where the image will land once it's ready.
 */
function CropSpinner() {
   return (
      <span
         role="status"
         aria-label={_js('Preparing image…')}
         className="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
      >
         <FaIcon
            name="spinner"
            className="size-6 animate-spin text-gray-500 motion-reduce:animate-none"
         />
      </span>
   );
}

function MediaTypeButton({
   active,
   onClick,
   children,
}: {
   active: boolean;
   onClick: () => void;
   children: ReactNode;
}) {
   return (
      <button
         type="button"
         onClick={onClick}
         className={cn(
            'rounded-md border px-3 py-1.5 text-sm font-medium',
            active
               ? 'border-brand-500 bg-brand-50 text-brand-700'
               : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
         )}
      >
         {children}
      </button>
   );
}

function IconButton({
   label,
   icon,
   onClick,
   disabled,
   dark,
}: {
   label: string;
   icon: string;
   onClick: () => void;
   disabled?: boolean;
   dark?: boolean;
}) {
   return (
      <IconActionButton
         label={label}
         icon={icon}
         onClick={onClick}
         disabled={disabled}
         className={cn(
            'size-6 disabled:opacity-30',
            dark ? 'text-gray-500 hover:text-gray-800' : 'text-white hover:bg-black/40'
         )}
      />
   );
}
